import { useAtomValue } from 'jotai';
import { lazy, Suspense } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { OktaAuth } from '@okta/okta-auth-js'; // Correct import
import { LoginCallback, Security } from '@okta/okta-react';
import { Group } from '@optiaxiom/react';

import {
  ConnectionProvider,
  FeatureFlag,
  FeatureFlagProvider,
  SelfConnectionProvider,
  SocketProvider,
} from '@frontend/contexts';

import CanInstance from './components/CanInstance';
import CanUser from './components/CanUser';
import CapabilityOrFlagGuard from './components/CapabilityOrFlagGuard';
import { ErrorBoundary } from './components/error-boundary';
import FullScreenLoader from './components/FullScreenLoader';
import { OptiGlobalHeader } from './components/optiGlobalHeader';
import { NotificationBellButton } from './components/optiGlobalHeader/components/NotificationBellButton';
import { OpalOverlayButton } from './components/optiGlobalHeader/components/OpalOverlayButton';
import { TrackerToggleButton } from './components/optiGlobalHeader/components/TrackerToggleButton';
import { VauSwitchButton } from './components/optiGlobalHeader/components/VauSwitchButton';
import PathNavigator from './components/PathNavigator';
import RequireAuth from './components/RequireAuth'; // Custom auth wrapper for protected pages
import RequireAuthOnly from './components/RequireAuthOnly';
import { ChatShell } from './features/chat/components/ChatShell';
import { BaseLayout } from './layouts/baseLayout';
import { isDesktopApp } from './utils/desktop';
import { SentryErrorBoundary } from './utils/sentry';
import { globalOptiHeaderVisibleAtom } from './atoms';
import { FeatureFlags, OVERLAY_PAGE_SEGMENTS, TRACKER_PAGE_SEGMENTS } from './constants';
import {
  useDesktopNavigation,
  useFontSize,
  useInstanceId,
  useOpalUser,
  useTheme,
  useUserPreferences,
} from './hooks';
import oktaAuth from './oktaAuth';

const AgentPage = lazy(() =>
  import(/* webpackChunkName: "AgentPage" */ './features').then(m => ({ default: m.AgentPage }))
);
const InstructionPage = lazy(() =>
  import(/* webpackChunkName: "InstructionPage" */ './features/userInstructions/components').then(
    m => ({ default: m.InstructionPage })
  )
);
const ContextPage = lazy(() =>
  import(/* webpackChunkName: "ContextPage" */ './features/context').then(m => ({
    default: m.ContextPage,
  }))
);
const BrandDetailPage = lazy(() =>
  import(
    /* webpackChunkName: "BrandDetailPage" */ './features/brands/components/BrandDetailPage'
  ).then(m => ({ default: m.BrandDetailPage }))
);
const SettingsPage = lazy(
  () => import(/* webpackChunkName: "SettingsPage" */ './features/settings/Settings')
);
const NotificationCenterPage = lazy(() =>
  import(/* webpackChunkName: "NotificationCenterPage" */ './features/notifications').then(m => ({
    default: m.NotificationCenterPage,
  }))
);
const ArtifactsPage = lazy(() =>
  import(/* webpackChunkName: "ArtifactsPage" */ './features/artifacts').then(m => ({
    default: m.ArtifactsPage,
  }))
);
const DirectoryPage = lazy(() =>
  import(
    /* webpackChunkName: "DirectoryPage" */ './features/directory/components/DirectoryPage'
  ).then(m => ({ default: m.DirectoryPage }))
);
const TeammateDetailsPage = lazy(() =>
  import(
    /* webpackChunkName: "TeammateDetailsPage" */ './features/vau/directory/components/TeammateDetailsPage'
  ).then(m => ({ default: m.TeammateDetailsPage }))
);
const SkillDirectoryDetailPage = lazy(() =>
  import(
    /* webpackChunkName: "SkillDirectoryDetailPage" */ './features/directory/components/SkillDirectoryDetailPage'
  ).then(m => ({ default: m.SkillDirectoryDetailPage }))
);
const HireOnboardingWizard = lazy(() =>
  import(
    /* webpackChunkName: "HireOnboardingWizard" */ './features/vau/directory/onboarding/components/HireOnboardingWizard'
  ).then(m => ({ default: m.HireOnboardingWizard }))
);
const EnableTeammatePage = lazy(() =>
  import(
    /* webpackChunkName: "EnableTeammatePage" */ './features/vau/directory/components/EnableTeammatePage'
  ).then(m => ({ default: m.EnableTeammatePage }))
);
const PluginsPage = lazy(() =>
  import(/* webpackChunkName: "PluginsPage" */ './features/plugins/components/PluginsPage').then(
    m => ({ default: m.PluginsPage })
  )
);
const ToolsManagement = lazy(() =>
  import(/* webpackChunkName: "ToolsManagement" */ './features').then(m => ({
    default: m.ToolsManagement,
  }))
);
const WorkflowExecutionPage = lazy(() =>
  import(
    /* webpackChunkName: "WorkflowExecutionPage" */ './features/agents/components/workflow-execution'
  ).then(m => ({
    default: m.WorkflowExecutionPage,
  }))
);
const ToolsWarningBanner = lazy(() =>
  import(
    /* webpackChunkName: "ToolsWarningBanner" */ './features/tools-management/components/ToolsWarningBanner/ToolsWarningBanner'
  ).then(m => ({
    default: m.ToolsWarningBanner,
  }))
);
const RedirectHandler = lazy(
  () => import(/* webpackChunkName: "RedirectHandler" */ './components/RedirectHandler')
);
const InstancePicker = lazy(() =>
  import(/* webpackChunkName: "InstancePicker" */ './features/instancePicker').then(m => ({
    default: m.InstancePicker,
  }))
);
const VAUPage = lazy(() =>
  import(/* webpackChunkName: "VAUPage" */ './features/vau/components/VAUPage').then(m => ({
    default: m.VAUPage,
  }))
);

function App() {
  return (
    <SentryErrorBoundary fallback={<p>An error has occurred</p>}>
      <WrappedSecurity />
    </SentryErrorBoundary>
  );
}

function WrappedSecurity() {
  const navigate = useNavigate();
  const location = useLocation();
  const { instanceId } = useInstanceId();
  const globalOptiHeaderVisible = useAtomValue(globalOptiHeaderVisibleAtom);
  useTheme();
  useFontSize();
  // Desktop shell forwards intercepted internal links here for client-side
  // navigation (no full reload). No-op in the web build.
  useDesktopNavigation();
  // Seed font_size + voice preferences atoms from the backend so settings
  // persist across browsers and devices, not just across refreshes on
  // the same machine (which atomWithStorage already handles).
  const { opalUser } = useOpalUser();
  useUserPreferences(opalUser.email);

  const segments = location.pathname.split('/');
  const isOnOverlayPage = OVERLAY_PAGE_SEGMENTS.some(s => segments.includes(s));
  // The Tracker toggle also shows on /messages (where IMs are clipped), even
  // though the Ask Opal overlay does not — see TRACKER_PAGE_SEGMENTS.
  const isOnTrackerPage = TRACKER_PAGE_SEGMENTS.some(s => segments.includes(s));
  const restoreOriginalUri = async (_oktaAuth: OktaAuth, originalUri?: string): Promise<void> => {
    navigate(originalUri || '/');
  };

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route path="/login" element={<LoginCallback />} />
        <Route
          path="/instances"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <InstancePicker />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/redirect"
          element={
            <RequireAuthOnly>
              <Suspense fallback={<FullScreenLoader />}>
                <RedirectHandler />
              </Suspense>
            </RequireAuthOnly>
          }
        />
        <Route
          path="/instances/:instanceId"
          element={
            <RequireAuth>
              <FeatureFlagProvider>
                <ErrorBoundary>
                  <OptiGlobalHeader visible={globalOptiHeaderVisible && !isDesktopApp()}>
                    <Group gap="8">
                      <NotificationBellButton />
                      {isOnTrackerPage && (
                        <FeatureFlag featureKey={FeatureFlags.VAU}>
                          <FeatureFlag featureKey={FeatureFlags.STICKYBOARD}>
                            <TrackerToggleButton />
                          </FeatureFlag>
                        </FeatureFlag>
                      )}
                      {isOnOverlayPage && <OpalOverlayButton />}
                      {/* Last in the slot so it sits as close as we can get to
                          the `?` help icon the ticket positions it against —
                          that icon is rendered inside opti-common-header, past
                          the edge of anything Opal controls. Self-gates on the
                          `vau` flag and on having a VAU to switch to. */}
                      <VauSwitchButton />
                    </Group>
                  </OptiGlobalHeader>
                </ErrorBoundary>
                <SocketProvider>
                  <Suspense fallback={null}>
                    <ToolsWarningBanner />
                  </Suspense>
                  <BaseLayout />
                </SocketProvider>
              </FeatureFlagProvider>
            </RequireAuth>
          }
        >
          <Route element={<ChatShell />}>
            <Route path="chat/*" element={null} />
            <Route path="messages/*" element={null} />
          </Route>
          <Route
            path="instructions/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="skills/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <ConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <InstructionPage />
                  </Suspense>
                </ConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="context/*"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ContextPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="brands/:brandId"
            element={
              <CanUser operations={['view_instructions', 'manage_instructions', 'use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.BRANDS}
                  fallback={<PathNavigator path={`/instances/${instanceId}/context`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <BrandDetailPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="artifacts/*"
            element={
              <CanUser operations={['use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ArtifactsPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="connectors/*"
            element={
              <CanUser operations={['view_tools', 'use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <ToolsManagement />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="settings/*"
            element={
              <CanUser operations={['use_chat']}>
                <Suspense fallback={<FullScreenLoader />}>
                  <SettingsPage />
                </Suspense>
              </CanUser>
            }
          />
          <Route
            path="notifications/*"
            element={
              <Suspense fallback={<FullScreenLoader />}>
                <NotificationCenterPage />
              </Suspense>
            }
          />
          <Route
            path="agents/workflow/executions/:executionId/*"
            element={
              <CanInstance capabilities={['workflows_full_enabled', 'workflows_chat_enabled']}>
                <CanUser operations={['view_agents']}>
                  <Suspense fallback={<FullScreenLoader />}>
                    <WorkflowExecutionPage />
                  </Suspense>
                </CanUser>
              </CanInstance>
            }
          />
          <Route
            path="agents/*"
            element={
              <CanUser operations={['view_agents']}>
                <SelfConnectionProvider>
                  <Suspense fallback={<FullScreenLoader />}>
                    <AgentPage />
                  </Suspense>
                </SelfConnectionProvider>
              </CanUser>
            }
          />
          <Route
            path="directory/teammates/:slug/enable"
            element={
              <CanUser operations={['manage_workflow_agents']}>
                <FeatureFlag
                  featureKey={FeatureFlags.DIRECTORY}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <EnableTeammatePage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="directory/teammates/:slug"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.DIRECTORY}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <TeammateDetailsPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="directory/teammates/:slug/onboard"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.DIRECTORY}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <HireOnboardingWizard />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="directory/skills/:directorySkillId"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.DIRECTORY}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <SkillDirectoryDetailPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="directory/*"
            element={
              <CanUser operations={['use_chat']}>
                <FeatureFlag
                  featureKey={FeatureFlags.DIRECTORY}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <DirectoryPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="plugins/*"
            element={
              <CanUser operations={['manage_instructions', 'manage_specialized_agents']}>
                <FeatureFlag
                  featureKey={FeatureFlags.PLUGINS}
                  fallback={<PathNavigator path={`/instances/${instanceId}/chat`} />}
                >
                  <Suspense fallback={<FullScreenLoader />}>
                    <PluginsPage />
                  </Suspense>
                </FeatureFlag>
              </CanUser>
            }
          />
          <Route
            path="vaus/*"
            // TODO: re-add capabilities={['vau_enabled']} once provisioned in Turnstile
            element={
              <CapabilityOrFlagGuard capabilities={[]} featureFlags={['vau']}>
                <CanUser operations={['manage_vt', 'view_agents']}>
                  <Suspense fallback={<FullScreenLoader />}>
                    <VAUPage />
                  </Suspense>
                </CanUser>
              </CapabilityOrFlagGuard>
            }
          />
          <Route path="" element={<PathNavigator path={`/instances/${instanceId}/chat`} />} />
        </Route>
        <Route
          path="*"
          element={
            !instanceId ? (
              <RequireAuthOnly>
                <PathNavigator path="/instances" />
              </RequireAuthOnly>
            ) : (
              <RequireAuth>
                <PathNavigator path={`/instances/${instanceId}/chat`} />
              </RequireAuth>
            )
          }
        />
      </Routes>
    </Security>
  );
}

export default App;
